import type { CurrentUser } from "@opendialysis/procare-api-schemas";

/**
 * Single owner of session state.
 *
 * The access token lives in a module-level variable and is NEVER written to
 * localStorage or sessionStorage. Its TTL is 300s (apps/api
 * .../authService.config.ts) and the httpOnly refresh cookie already survives
 * reloads, so persisting it buys nothing and hands any XSS a live credential.
 * Session restore on load is POST /auth/refresh -> GET /auth/me.
 *
 * Exposed to React through useSyncExternalStore (see useSession) so the
 * interceptors — which are not React code — can read and write it directly.
 */

export type SessionStatus = "bootstrapping" | "authenticated" | "anonymous";

export type SessionSnapshot = {
  status: SessionStatus;
  user: CurrentUser | null;
};

let accessToken: string | null = null;
/** Absolute epoch SECONDS, as the API sends it — not a duration. */
let tokenExpireAt: number | null = null;
let currentUser: CurrentUser | null = null;
let status: SessionStatus = "bootstrapping";

// useSyncExternalStore compares snapshots by reference, so hand out a cached
// object and only replace it when something actually changed. Returning a
// fresh object each call is an infinite render loop.
let snapshot: SessionSnapshot = { status, user: currentUser };

const listeners = new Set<() => void>();

function emit() {
  snapshot = { status, user: currentUser };
  for (const listener of listeners) listener();
}

export function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getSnapshot(): SessionSnapshot {
  return snapshot;
}

export const getAccessToken = () => accessToken;
export const getTokenExpireAt = () => tokenExpireAt;

/** Records the tokens from a login or refresh. Identity comes from /auth/me. */
export function setTokens(token: string, expireAt: number) {
  accessToken = token;
  tokenExpireAt = expireAt;
}

export function setCurrentUser(user: CurrentUser) {
  currentUser = user;
  status = "authenticated";
  emit();
}

/** Bootstrap finished without a usable cookie: the visitor is signed out. */
export function setAnonymous() {
  if (status === "anonymous" && !accessToken) return;
  accessToken = null;
  tokenExpireAt = null;
  currentUser = null;
  status = "anonymous";
  emit();
}

/**
 * The ONE way a session ends — user-initiated sign-out and forced sign-out (a
 * 401 whose refresh failed) both come through here.
 *
 * @param onCleared runs before listeners are notified; used to clear the query
 * cache, which cannot be imported here without a cycle (queryClient -> apiError
 * -> ... -> session).
 * @returns whether a session was actually active, so callers show
 * "session expired" exactly once.
 */
export function endSession(onCleared?: () => void): boolean {
  const wasActive = status === "authenticated";
  accessToken = null;
  tokenExpireAt = null;
  currentUser = null;
  status = "anonymous";
  onCleared?.();
  emit();
  return wasActive;
}

/** Test-only: rewind to the pre-bootstrap state. */
export function resetSessionForTests() {
  accessToken = null;
  tokenExpireAt = null;
  currentUser = null;
  status = "bootstrapping";
  snapshot = { status, user: currentUser };
  listeners.clear();
}
